'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import logo from '@/assets/logo.png';
import bgImage from '@/assets/background.png';
import { appConfig } from '@/config/app.config';

interface BrandedLoaderProps {
  /** Small uppercase status line shown in the pill, e.g. "Loading dashboard..." */
  status?: string;
}

/**
 * Full-screen branded loading state shown while auth/session is being
 * resolved (between login and the dashboard actually rendering).
 */
export function BrandedLoader({ status = 'Loading dashboard...' }: BrandedLoaderProps) {
  // Mirrors Header.tsx: prefer the tenant name cached at login, fall back to the default.
  const [councilName, setCouncilName] = useState<string>(appConfig.app.municipalCorporation);

  useEffect(() => {
    const cached = localStorage.getItem('councilName');
    if (cached) setCouncilName(cached);
  }, []);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      {/* Light, blurred background so the loader doesn't feel like a bare white page */}
      <div className="absolute inset-0 pointer-events-none">
        <Image
          src={bgImage}
          alt=""
          fill
          priority
          className="object-cover scale-110 blur-2xl opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50/80 via-blue-50/60 to-slate-100/80" />
      </div>

      <div className="relative flex flex-col items-center gap-4">
        <div className="relative h-24 w-24">
          {/* Spinning ring around the logo */}
          <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin" />
          {/* Logo */}
          <div className="absolute inset-[6px] rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center overflow-hidden">
            <Image
              src={logo}
              alt={`${appConfig.app.municipalCorporation} logo`}
              className="h-[70%] w-[70%] object-contain"
              priority
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-0.5 text-center">
          <p suppressHydrationWarning className="text-base font-bold text-slate-800 tracking-tight">
            {councilName}
          </p>
          <p className="text-xs text-slate-500 font-medium">{appConfig.app.name}</p>
        </div>

        <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-100 px-3 py-1 rounded-full">
          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" />
          <span className="text-[10px] font-bold text-blue-700 tracking-wide uppercase">{status}</span>
        </div>
      </div>
    </div>
  );
}
